// src/pages/emision/ImportPolizasModal.jsx
// Modal de importación de pólizas emitidas por la compañía para una rendición
// individual: elegir archivo → previsualizar clasificación → confirmar import.
import { useState } from 'react';
import rendicionService from '../../services/rendicionService';

const FILA_PILL = {
  ok:            { bg: '#E1F1E7', color: '#0A7C46', label: 'Lista' },
  sin_poliza:    { bg: '#FBF1CC', color: '#8A5A0B', label: 'Sin N° póliza' },
  no_encontrado: { bg: '#FEE2E2', color: '#B91C1C', label: 'No encontrado' },
  ya_emitido:    { bg: '#E4E4E2', color: '#6B6F72', label: 'Ya emitido' },
  duplicado:     { bg: '#FEE2E2', color: '#B91C1C', label: 'Duplicado' },
};

export default function ImportPolizasModal({ rendicion, onClose, onImported }) {
  const [csvText,   setCsvText]   = useState(null);
  const [fileName,  setFileName]  = useState(null);
  const [preview,   setPreview]   = useState(null);   // { filas, resumen }
  const [resultado, setResultado] = useState(null);   // { ok, errors, estado, total_polizas_recibidas }
  const [busy,      setBusy]      = useState(false);
  const [errMsg,    setErrMsg]    = useState(null);

  const okCount = preview?.resumen?.ok ?? 0;

  async function handleFile(file) {
    setErrMsg(null);
    setBusy(true);
    try {
      const text = await file.text();
      const p = await rendicionService.previsualizarRespuesta(rendicion.id, text);
      setCsvText(text);
      setFileName(file.name);
      setPreview(p);
    } catch (err) {
      setErrMsg(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleImportar() {
    setErrMsg(null);
    setBusy(true);
    try {
      const r = await rendicionService.recibirRespuesta(rendicion.id, csvText);
      setResultado(r);
    } catch (err) {
      setErrMsg(err.message);
    } finally {
      setBusy(false);
    }
  }

  // Refrescar la página recién al cerrar: si se refresca durante la importación,
  // RendicionesPage entra en "Cargando…" y desmonta/remonta este modal en blanco.
  function handleClose() {
    if (resultado) onImported?.();
    onClose();
  }

  const btn = (primary) => ({
    padding: '9px 18px', fontSize: 13, fontWeight: 600, borderRadius: 9, border: 'none',
    cursor: busy ? 'not-allowed' : 'pointer',
    background: primary ? (busy ? '#9BD0C9' : '#007D6E') : '#F1F1EF',
    color: primary ? '#fff' : '#54585A',
  });

  return (
    <div
      onClick={handleClose}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(14,17,22,.44)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, width: 'min(760px, 100%)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #EFEFEE', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.02em', margin: 0 }}>Recibir pólizas emitidas</h2>
            <p style={{ fontSize: 12.5, color: '#8D8D8D', margin: '4px 0 0' }}>
              {rendicion.companias?.nombre_corto ?? 'Compañía'} · {rendicion.total_certificados ?? '—'} certificados en la rendición
            </p>
          </div>
          <button onClick={handleClose} style={{ all: 'unset', cursor: 'pointer', fontSize: 20, color: '#8D8D8D', padding: 6, lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {errMsg && (
            <div style={{ marginBottom: 14, padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, fontSize: 13, color: '#B91C1C' }}>
              {errMsg}
            </div>
          )}

          {/* Paso 3: resultado */}
          {resultado ? (
            <div style={{ textAlign: 'center', padding: '18px 0' }}>
              <div style={{ width: 52, height: 52, margin: '0 auto', borderRadius: 14, background: '#E1F1E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>✓</div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: '16px 0 0' }}>{resultado.ok.length} pólizas importadas</h3>
              <p style={{ fontSize: 13, color: '#54585A', margin: '8px 0 0' }}>
                {resultado.errors.length > 0 && `${resultado.errors.length} filas con error · `}
                {resultado.total_polizas_recibidas} de {rendicion.total_certificados ?? '—'} recibidas ·
                estado: {resultado.estado === 'respondida' ? 'Respondida' : 'Enviada (parcial)'}
              </p>
              {resultado.errors.length > 0 && (
                <ul style={{ textAlign: 'left', margin: '14px auto 0', maxWidth: 480, fontSize: 12, color: '#B91C1C', paddingLeft: 18 }}>
                  {resultado.errors.slice(0, 10).map((e, i) => <li key={i}>{e.certificado_id}: {e.error}</li>)}
                  {resultado.errors.length > 10 && <li>… y {resultado.errors.length - 10} más</li>}
                </ul>
              )}
            </div>
          ) : !preview ? (
            /* Paso 1: elegir archivo */
            <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
              <p style={{ fontSize: 13.5, color: '#54585A', margin: '0 0 18px', lineHeight: 1.55 }}>
                Sube el archivo que devolvió la compañía con los números de póliza.
                Debe incluir <code>certificado_id</code> o <code>numero_certificado</code> y <code>numero_poliza</code>.
              </p>
              <label style={{ ...btn(true), display: 'inline-flex', cursor: busy ? 'wait' : 'pointer' }}>
                {busy ? 'Analizando…' : 'Elegir archivo CSV'}
                <input type="file" accept=".csv,.txt" style={{ display: 'none' }} disabled={busy} onChange={e => {
                  if (e.target.files?.[0]) handleFile(e.target.files[0]);
                  e.target.value = '';
                }} />
              </label>
              <p style={{ fontSize: 12.5, margin: '18px 0 0' }}>
                <button
                  onClick={async () => {
                    try {
                      await rendicionService.descargarArchivo(
                        rendicionService.getModeloUrl(rendicion.id),
                        `modelo_respuesta_${rendicion.id}.csv`
                      );
                    } catch (err) {
                      alert(`No se pudo descargar el modelo: ${err.message}`);
                    }
                  }}
                  style={{ all: 'unset', cursor: 'pointer', color: '#007D6E', textDecoration: 'underline' }}
                >
                  Descargar modelo de importación
                </button>
                <span style={{ color: '#8D8D8D' }}> — plantilla con los certificados pendientes para que la compañía complete el N° de póliza.</span>
              </p>
            </div>
          ) : (
            /* Paso 2: previsualización */
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <p style={{ fontSize: 13, color: '#54585A', margin: 0 }}>
                  <strong style={{ color: '#1A1A1A' }}>{fileName}</strong> · {preview.filas.length} filas
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {Object.entries(preview.resumen).map(([estado, n]) => {
                    const p = FILA_PILL[estado] ?? FILA_PILL.no_encontrado;
                    return (
                      <span key={estado} style={{ fontSize: 11.5, fontWeight: 600, borderRadius: 20, padding: '3px 10px', background: p.bg, color: p.color }}>
                        {n} {p.label}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div style={{ border: '1px solid #EFEFEE', borderRadius: 12, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ background: '#FAFAF9', borderBottom: '1px solid #EFEFEE' }}>
                      {['Certificado', 'Cliente', 'N° Póliza', 'Estado'].map(h => (
                        <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: '#A0A09E' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.filas.map((f, i) => {
                      const p = FILA_PILL[f.estado] ?? FILA_PILL.no_encontrado;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #F3F3F2' }}>
                          <td style={{ padding: '9px 14px', fontFamily: 'ui-monospace,monospace', fontSize: 11.5, color: '#54585A' }}>{f.codigo ?? f.certificado_id ?? '—'}</td>
                          <td style={{ padding: '9px 14px', color: '#1A1A1A' }}>{f.cliente_nombre ?? '—'}</td>
                          <td style={{ padding: '9px 14px', fontFamily: 'ui-monospace,monospace', fontSize: 11.5, fontWeight: 600, color: '#1A1A1A' }}>{f.numero_poliza ?? '—'}</td>
                          <td style={{ padding: '9px 14px' }}>
                            <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '2px 9px', background: p.bg, color: p.color, whiteSpace: 'nowrap' }}>{p.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #EFEFEE', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {resultado ? (
            <button onClick={handleClose} style={btn(true)}>Cerrar</button>
          ) : preview ? (
            <>
              <button disabled={busy} onClick={() => { setPreview(null); setCsvText(null); setFileName(null); }} style={btn(false)}>
                Elegir otro archivo
              </button>
              <button disabled={busy || okCount === 0} onClick={handleImportar} style={{ ...btn(true), opacity: okCount === 0 ? .55 : 1 }}>
                {busy ? 'Importando…' : `Importar ${okCount} póliza${okCount === 1 ? '' : 's'}`}
              </button>
            </>
          ) : (
            <button onClick={handleClose} style={btn(false)}>Cancelar</button>
          )}
        </div>
      </div>
    </div>
  );
}
